import React, { FC, useState } from 'react';

import { PageTitle, PageWrapper } from '../../../../components';
import { useHeroToast } from '../../../../hooks';
import { useSaveInMailTemplate } from '../../../../queries';
import type { PreviewDevice } from '../components';
import {
  AiTemplateModal, BlockList, BlockPalette, BuilderTopBar, EmailPreview, Inspector,
  SendCampaignDrawer, TemplatePicker,
} from '../components';
import { useEmailBuilderStore } from '../model/store';
import type { EmailBlock, EmailDocument } from '../model/types';
import { renderEmailHtml } from '../render/renderEmailHtml';
import { EMAIL_TEMPLATES } from '../templates';

/** Nazwa pliku przy pobraniu. Bez znaków, które psują ścieżki na dysku. */
const fileName = (name: string): string => `${name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'wiadomosc'}.html`;

export const EmailBuilderPage: FC = () => {
  const doc = useEmailBuilderStore((state) => state.doc);
  const selectedId = useEmailBuilderStore((state) => state.selectedId);
  const past = useEmailBuilderStore((state) => state.past);
  const future = useEmailBuilderStore((state) => state.future);
  const store = useEmailBuilderStore();

  const [device, setDevice] = useState<PreviewDevice>('desktop');
  /** Id wpisu w bazie. Puste, dopóki szablon nie został ani razu zapisany. */
  const [savedId, setSavedId] = useState<string | null>(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const { showError, showSuccess } = useHeroToast();
  const { mutateAsync: saveTemplate, isPending: isSaving } = useSaveInMailTemplate();

  const handleStart = (nextDoc: Parameters<typeof store.start>[0], id: string | null) => {
    store.start(nextDoc);
    setSavedId(id);
  };

  const handlePick = (templateId: string) => {
    const template = EMAIL_TEMPLATES.find((item) => item.id === templateId);
    if (template) handleStart(template.build(), null);
  };

  const handleSave = async () => {
    if (!doc) return;
    try {
      const id = await saveTemplate({
        id: savedId,
        data: { name: doc.name, subject: doc.subject, doc },
      });
      setSavedId(id);
      showSuccess(savedId ? 'Zmiany zapisane.' : 'Szablon zapisany.');
    } catch {
      showError('Nie udało się zapisać szablonu.');
    }
  };

  const handleDownload = () => {
    if (!doc) return;
    const blob = new Blob([renderEmailHtml(doc)], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName(doc.name);
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleCopy = async () => {
    if (!doc) return;
    await navigator.clipboard.writeText(renderEmailHtml(doc));
  };

  if (!doc) {
    return (
      <PageWrapper>
        <PageTitle title="Kreator wiadomości" />
        <TemplatePicker
          onPick={handlePick}
          onOpenSaved={(record) => handleStart(record.doc as EmailDocument, record.id)}
          onGenerateWithAi={() => setAiOpen(true)}
        />
        {aiOpen && (
          <AiTemplateModal
            onClose={() => setAiOpen(false)}
            onReady={(generated) => {
              handleStart(generated, null);
              setAiOpen(false);
            }}
          />
        )}
      </PageWrapper>
    );
  }

  const selected: EmailBlock | null = doc.blocks.find((block) => block.id === selectedId) ?? null;

  return (
    <PageWrapper>
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white" data-email-builder>
        <BuilderTopBar
          doc={doc}
          device={device}
          canUndo={past.length > 0}
          canRedo={future.length > 0}
          onDevice={setDevice}
          onMeta={store.updateMeta}
          onUndo={store.undo}
          onRedo={store.redo}
          onClose={store.close}
          onCopyHtml={handleCopy}
          onDownloadHtml={handleDownload}
          onSave={handleSave}
          onSend={() => setSendOpen(true)}
          isSaving={isSaving}
          savedId={savedId}
        />

        {sendOpen && (
          <SendCampaignDrawer doc={doc} templateId={savedId} onClose={() => setSendOpen(false)} />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)_340px]">
          <aside className="flex max-h-[calc(100vh-220px)] flex-col gap-5 overflow-auto border-r border-gray-200 p-4">
            <BlockPalette onAdd={(kind) => store.addBlock(kind, selectedId)} />
            <BlockList
              blocks={doc.blocks}
              selectedId={selectedId}
              onSelect={store.select}
              onMove={store.moveBlock}
              onDuplicate={store.duplicateBlock}
              onRemove={store.removeBlock}
            />
          </aside>

          <div className="max-h-[calc(100vh-220px)] overflow-hidden">
            <EmailPreview doc={doc} device={device} />
          </div>

          <aside className="flex max-h-[calc(100vh-220px)] flex-col gap-4 overflow-auto border-l border-gray-200 p-4">
            <button
              type="button"
              className="self-start text-xs font-medium text-gray-500 underline"
              onClick={() => store.select(null)}
            >
              Ustawienia całej wiadomości
            </button>
            <Inspector
              block={selected}
              theme={doc.theme}
              onChange={(patch) => selected && store.updateBlock(selected.id, patch)}
              onThemeChange={store.updateTheme}
            />
          </aside>
        </div>
      </div>
    </PageWrapper>
  );
};
